/* ---------------------------------------------------------------------
 *  TelemetryTimeSeriesPlot  --  FIFO scrolling strip-chart
 *
 *  Cardiogram / seismograph / uTorrent-style canvas time-series:
 *  new samples appear on the right edge, the waveform scrolls left,
 *  oldest data falls off the left -- fixed time-window, zero jitter.
 *
 *  - Solid-black background, dark grid, monospace axis labels
 *  - Neon waveform stroke with subtle glow
 *  - Auto-ranging Y-axis with nice grid steps per channel
 *  - Canvas is position:absolute inside the container -- never
 *    influences parent height, eliminates resize / scrollbar loops
 *  - DPR-aware (retina crisp)
 *
 *  PAC Glass design system -- surface is foundation bg-black, no
 *  redundant panels; border is rgba(255,255,255,0.06).
 * ------------------------------------------------------------------ */

import React, { useEffect, useRef } from 'react';

/* -- Public API ------------------------------------------------------- */

export interface TimeSeriesPoint {
  ts: number;    // epoch-ms
  value: number;
}

/** Vertical marker — rendered as a dashed vertical line with a label pill. */
export interface PlotMarker {
  ts: number;       // epoch-ms
  label: string;    // text rendered in the pill
  color?: string;   // default: purple #a855f7
}

export interface TelemetryTimeSeriesPlotProps {
  /** Sorted (oldest -> newest) array of { ts, value }. */
  data: TimeSeriesPoint[];
  /** Plot title rendered top-left. */
  title: string;
  /** Unit label appended to Y-axis ticks (e.g. " C", " dBm"). */
  unit?: string;
  /** Neon stroke colour (default: emerald #34d399). */
  color?: string;
  /** Optional CSS class on the outer container. */
  className?: string;
  /** Fixed height in CSS px (default 160). */
  minHeight?: number;
  /** Visible time-window in seconds (default 300 = 5 min). */
  windowSec?: number;
  /** Optional extra series overlays to draw (label + points + color). */
  overlays?: Array<{ label: string; data: TimeSeriesPoint[]; color?: string }>;
  /** Shared hover-timestamp ref for cross-plot synchronisation.
   *  When one plot is hovered all connected plots draw a crosshair at the same ts. */
  hoverTsRef?: React.MutableRefObject<number | null>;
  /** Hard floor for the Y-axis (e.g. -100 for RSSI). Auto-scale will never go below this. */
  yFloor?: number;
  /** Hard ceiling for the Y-axis (e.g. 100 for humidity %). Auto-scale will never exceed this. */
  yCeil?: number;
  /** Optional anchor epoch-ms for the right edge of the time axis.
   *  When set, replaces Date.now() so historical data fills the plot
   *  instead of being pushed off-screen to the left. */
  anchorTs?: number;
  /** Optional vertical markers (e.g. run-start events). */
  markers?: PlotMarker[];
}

/* -- Grid / layout constants ------------------------------------------ */

const PAD = { left: 56, right: 20, top: 28, bottom: 36 } as const;
const H_LINES = 5;
const GRID_COLOR   = '#1a1e24';
const ZERO_COLOR   = '#30363d';
const LABEL_COLOR  = '#484f58';
const TITLE_COLOR  = '#8b949e';
const BG           = '#000000';

/** Vertical grid divisions — adapts to the chosen time window. */
function vGridLines(windowMs: number): number {
  return windowMs > 86_400_000 ? 7 : 6;   // 7 for 1 W+, 6 otherwise
}

/* -- Time formatting helpers ------------------------------------------ */

/** Window-aware axis label — wider windows get shorter stamps. */
function fmtTime(epochMs: number, windowMs: number = 0): string {
  const d = new Date(epochMs);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  if (windowMs <= 3_600_000)  return `${hh}:${mm}:${ss}`;           // ≤ 1 H
  if (windowMs <= 86_400_000) return `${hh}:${mm}`;                  // ≤ 1 D
  const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  return `${days[d.getDay()]} ${hh}:${mm}`;                          // > 1 D
}

function niceStep(range: number, ticks: number): number {
  if (range <= 0 || ticks <= 0) return 1;
  const raw = range / ticks;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const frac = raw / mag;
  if (frac <= 1)   return mag;
  if (frac <= 2)   return 2 * mag;
  if (frac <= 5)   return 5 * mag;
  return 10 * mag;
}

/** Convert 6-char hex (#rrggbb) to rgba string. */
function hexToRgba(hex: string, a: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

/** Live "time ago" tooltip — updates every frame since drawFrame runs in RAF. */
function fmtTimeTooltip(epochMs: number): string {
  const diffMs = Date.now() - epochMs;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ${sec % 60}s ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ${min % 60}m ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ${hr % 24}h ago`;
}

/** Linear interpolation of value at `ts` within a sorted series. */
function interpolateAt(series: TimeSeriesPoint[], ts: number): number | null {
  if (!series || series.length === 0) return null;
  if (ts <= series[0].ts) return series[0].value;
  if (ts >= series[series.length - 1].ts) return series[series.length - 1].value;
  for (let i = 1; i < series.length; i++) {
    if (series[i].ts >= ts) {
      const a = series[i - 1], b = series[i];
      const t = (ts - a.ts) / (b.ts - a.ts);
      return a.value + t * (b.value - a.value);
    }
  }
  return null;
}

/** Draw a rounded-rect path (no fill/stroke — caller decides). */
function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/* -- Canvas draw ------------------------------------------------------- */

function drawFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  data: TimeSeriesPoint[],
  title: string,
  unit: string,
  color: string,
  overlays: Array<{ label: string; data: TimeSeriesPoint[]; color?: string }>,
  windowMs: number,
  hoverTs: number | null,
  yFloor?: number,
  yCeil?: number,
  anchorTs?: number,
  markers?: PlotMarker[],
): number {
  /* -- Background ---------------------------------------------------- */
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, w, h);

  const plotW = w - PAD.left - PAD.right;
  const plotH = h - PAD.top  - PAD.bottom;
  const cx = PAD.left;
  const cy = PAD.top;

  if (plotW < 10 || plotH < 10) return Date.now();

  /* -- Time window --------------------------------------------------- *
   * Right edge anchored to now (or anchorTs for historical views).
   * Data always scrolls left; the right edge is the current wall-clock
   * so the cardiogram feels continuous — no gap on the right.           */
  const tMax = anchorTs ?? Date.now();
  const tMin = tMax - windowMs;

  /* -- Filter visible points (within window + one extra each side) --- */
  let visStart = 0;
  for (let i = 0; i < data.length; i++) {
    if (data[i].ts >= tMin) { visStart = Math.max(0, i - 1); break; }
    visStart = i;
  }
  const visible = data.slice(visStart);

  /* -- Grid ---------------------------------------------------------- */
  ctx.strokeStyle = GRID_COLOR;
  ctx.lineWidth = 1;
  for (let i = 0; i <= H_LINES; i++) {
    const y = Math.round(cy + (plotH * i) / H_LINES) + 0.5;
    ctx.beginPath(); ctx.moveTo(cx, y); ctx.lineTo(cx + plotW, y); ctx.stroke();
  }
  const vl = vGridLines(windowMs);
  for (let i = 0; i <= vl; i++) {
    const x = Math.round(cx + (plotW * i) / vl) + 0.5;
    ctx.beginPath(); ctx.moveTo(x, cy); ctx.lineTo(x, cy + plotH); ctx.stroke();
  }

  /* -- Title --------------------------------------------------------- */
  ctx.fillStyle = TITLE_COLOR;
  ctx.font = '11px "SF Mono", "Cascadia Code", Menlo, monospace';
  ctx.textAlign = 'left';
  ctx.fillText(title, cx, cy - 10);

  /* -- Legend for overlays (top-right) ------------------------------ */
  if (overlays && overlays.length) {
    ctx.font = '10px "SF Mono", "Cascadia Code", Menlo, monospace';
    ctx.textAlign = 'right';
    let x = cx + plotW - 6;
    for (let i = overlays.length - 1; i >= 0; i--) {
      const ov = overlays[i];
      const label = ov.label || 'dev';
      const m = ctx.measureText(label);
      const itemW = 12 + 4 + m.width + 8; // swatch + gap + text + padding
      x -= itemW;
      // swatch
      ctx.fillStyle = ov.color ?? '#9ca3af';
      ctx.fillRect(x + 6, cy - 20, 10, 10);
      // label
      ctx.fillStyle = TITLE_COLOR;
      ctx.textAlign = 'left';
      ctx.fillText(label, x + 6 + 14, cy - 11);
      ctx.textAlign = 'right';
    }
  }

  /* -- Value range (auto-scale to visible data, or +/-1 if empty) ---- */
  let vMin = Infinity, vMax = -Infinity;
  // main series
  for (const p of visible) { if (p.value < vMin) vMin = p.value; if (p.value > vMax) vMax = p.value; }
  // overlays series within time window
  if (overlays && overlays.length) {
    for (const ov of overlays) {
      for (const p of ov.data) {
        if (p.ts < tMin || p.ts > tMax) continue;
        if (p.value < vMin) vMin = p.value;
        if (p.value > vMax) vMax = p.value;
      }
    }
  }
  const hasData = vMin !== Infinity && Number.isFinite(vMin);
  if (!hasData || !Number.isFinite(vMin)) { vMin = -1; vMax = 1; }
  if (vMax - vMin < 0.001) { vMin -= 1; vMax += 1; }

  /* 100 % headroom — ceil = max + range, floor = min − range.
     Industrial-grade: waveform occupies the central band, never hugs edges. */
  const rawSpan = vMax - vMin;
  const headroom = Math.max(rawSpan * 1.0, 0.5);
  vMin -= headroom;
  vMax += headroom;

  /* Hard clamp — physical / protocol boundaries (e.g. 0–100 %RH, -100…3 dBm) */
  if (yFloor !== undefined && vMin < yFloor) vMin = yFloor;
  if (yCeil  !== undefined && vMax > yCeil)  vMax = yCeil;

  // Round to nice grid
  const step = niceStep(vMax - vMin, H_LINES);
  vMin = Math.floor(vMin / step) * step;
  vMax = Math.ceil(vMax / step) * step;

  /* Re-apply hard clamp after grid rounding (rounding can overshoot) */
  if (yFloor !== undefined && vMin < yFloor) vMin = yFloor;
  if (yCeil  !== undefined && vMax > yCeil)  vMax = yCeil;
  const vRange = vMax - vMin || 1;

  /* -- Y-axis labels ------------------------------------------------- */
  ctx.fillStyle = LABEL_COLOR;
  ctx.font = '10px "SF Mono", "Cascadia Code", Menlo, monospace';
  ctx.textAlign = 'right';
  for (let i = 0; i <= H_LINES; i++) {
    const val = vMax - (vRange * i) / H_LINES;
    const y = cy + (plotH * i) / H_LINES;
    const label = Math.abs(val) >= 100 ? String(Math.round(val))
      : Number.isInteger(val) ? String(val)
      : val.toFixed(1);
    ctx.fillText(label + unit, cx - 6, y + 4);
  }

  /* -- X-axis labels (scrolling wall-clock, window-aware format) ----- */
  ctx.textAlign = 'center';
  for (let i = 0; i <= vl; i++) {
    const t = tMin + (windowMs * i) / vl;
    const x = cx + (plotW * i) / vl;
    ctx.fillText(fmtTime(t, windowMs), x, cy + plotH + 16);
  }

  /* -- Day-change markers (vertical rule at every 00:00) ------------- */
  {
    // Find the first midnight >= tMin
    const dStart = new Date(tMin);
    dStart.setHours(0, 0, 0, 0);
    if (dStart.getTime() < tMin) dStart.setDate(dStart.getDate() + 1);

    const DAY_MS = 86_400_000;
    for (let midTs = dStart.getTime(); midTs <= tMax; midTs += DAY_MS) {
      const mx = cx + ((midTs - tMin) / windowMs) * plotW;
      if (mx < cx || mx > cx + plotW) continue;

      // Vertical dashed marker line
      ctx.save();
      ctx.strokeStyle = 'rgba(250,204,21,0.35)'; // amber-300 @ 35 %
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(Math.round(mx) + 0.5, cy);
      ctx.lineTo(Math.round(mx) + 0.5, cy + plotH);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();

      // Label: <yymmdd>.<timeago>
      const md = new Date(midTs);
      const yy = String(md.getFullYear()).slice(-2);
      const mo = String(md.getMonth() + 1).padStart(2, '0');
      const dd = String(md.getDate()).padStart(2, '0');
      const tag = `${yy}${mo}${dd}`;
      const ago = fmtTimeTooltip(midTs);         // "2h 15m ago" — repaints every RAF tick
      const markerLabel = `${tag}.${ago}`;

      ctx.save();
      ctx.font = '9px "SF Mono", "Cascadia Code", Menlo, monospace';
      const tw = ctx.measureText(markerLabel).width;
      const lpad = 4, ltop = 2, lh = 13;
      // Position label just right of the line (or left if near right edge)
      let lx = mx + 4;
      if (lx + tw + lpad * 2 > cx + plotW) lx = mx - tw - lpad * 2 - 4;
      const ly = cy + 2;

      // Glass pill background
      ctx.fillStyle = 'rgba(0,0,0,0.72)';
      ctx.strokeStyle = 'rgba(250,204,21,0.25)';
      ctx.lineWidth = 1;
      roundedRect(ctx, lx, ly, tw + lpad * 2, lh + ltop * 2, 3);
      ctx.fill();
      ctx.stroke();

      // Label text
      ctx.fillStyle = 'rgba(250,204,21,0.8)';
      ctx.textAlign = 'left';
      ctx.fillText(markerLabel, lx + lpad, ly + ltop + lh - 2);
      ctx.restore();
    }
  }

  /* -- Custom markers (e.g. run-start events) — purple dashed line ---- */
  if (markers && markers.length) {
    for (const mk of markers) {
      if (mk.ts < tMin || mk.ts > tMax) continue;
      const mx = cx + ((mk.ts - tMin) / windowMs) * plotW;
      const mc = mk.color || '#a855f7'; // purple-500

      // Dashed vertical line
      ctx.save();
      ctx.strokeStyle = hexToRgba(mc, 0.55);
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(Math.round(mx) + 0.5, cy);
      ctx.lineTo(Math.round(mx) + 0.5, cy + plotH);
      ctx.stroke();
      ctx.setLineDash([]);

      // Diamond indicator at bottom
      const dmY = cy + plotH - 6;
      ctx.fillStyle = mc;
      ctx.beginPath();
      ctx.moveTo(mx,     dmY - 4);
      ctx.lineTo(mx + 4, dmY);
      ctx.lineTo(mx,     dmY + 4);
      ctx.lineTo(mx - 4, dmY);
      ctx.closePath();
      ctx.fill();

      // Label pill
      ctx.font = '9px "SF Mono", "Cascadia Code", Menlo, monospace';
      const mtw = ctx.measureText(mk.label).width;
      const mpad = 4, mlh = 13;
      let mlx = mx + 4;
      if (mlx + mtw + mpad * 2 > cx + plotW) mlx = mx - mtw - mpad * 2 - 4;
      const mly = cy + plotH - 24;

      ctx.fillStyle = 'rgba(0,0,0,0.78)';
      ctx.strokeStyle = hexToRgba(mc, 0.35);
      ctx.lineWidth = 1;
      roundedRect(ctx, mlx, mly, mtw + mpad * 2, mlh + 4, 3);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = hexToRgba(mc, 0.9);
      ctx.textAlign = 'left';
      ctx.fillText(mk.label, mlx + mpad, mly + mlh);
      ctx.restore();
    }
  }

  if (!hasData) {
    ctx.fillStyle = '#363b44';
    ctx.font = '11px "SF Mono", "Cascadia Code", Menlo, monospace';
    ctx.textAlign = 'center';
    ctx.fillText('awaiting data', cx + plotW / 2, cy + plotH / 2);
    return tMax;
  }

  /* -- Zero / centre dashed line ------------------------------------- */
  if (vMin < 0 && vMax > 0) {
    const zeroY = cy + plotH * (1 - (0 - vMin) / vRange);
    ctx.strokeStyle = ZERO_COLOR;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath(); ctx.moveTo(cx, zeroY); ctx.lineTo(cx + plotW, zeroY); ctx.stroke();
    ctx.setLineDash([]);
  }

  /* -- Clip inside the plot area ------------------------------------- */
  ctx.save();
  ctx.beginPath();
  ctx.rect(cx, cy, plotW, plotH);
  ctx.clip();
  /* -- Draw helper for series ----------------------------------------
   * Uses monotone cubic Bezier curves (Fritsch-Carlson tangents) for
   * smooth, overshoot-free waveform rendering.  Eliminates the
   * "laddering" / staircase artefact produced by linear lineTo().
   * ----------------------------------------------------------------- */
  const drawSeries = (series: TimeSeriesPoint[], strokeColor: string, opts?: { fill?: boolean }) => {
    if (!series || series.length === 0) return;
    // Include one boundary point each side for seamless edge-to-edge lines
    let si = 0, ei = series.length - 1;
    for (let i = 0; i < series.length; i++) {
      if (series[i].ts >= tMin) { si = Math.max(0, i - 1); break; }
      si = i;
    }
    for (let i = series.length - 1; i >= 0; i--) {
      if (series[i].ts <= tMax) { ei = Math.min(series.length - 1, i + 1); break; }
      ei = i;
    }
    const vis = series.slice(si, ei + 1);
    if (vis.length === 0) return;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 1.8;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.shadowColor = strokeColor;
    ctx.shadowBlur = 6;

    // Pre-compute pixel coordinates
    const n = vis.length;
    const pxX: number[] = new Array(n);
    const pxY: number[] = new Array(n);
    for (let i = 0; i < n; i++) {
      pxX[i] = cx + ((vis[i].ts - tMin) / windowMs) * plotW;
      pxY[i] = cy + plotH * (1 - (vis[i].value - vMin) / vRange);
    }

    ctx.beginPath();
    ctx.moveTo(pxX[0], pxY[0]);

    if (n < 3) {
      // Too few points for Bezier — fall back to linear
      for (let i = 1; i < n; i++) ctx.lineTo(pxX[i], pxY[i]);
    } else {
      // Monotone cubic Bezier (Fritsch-Carlson tangents in pixel space)
      const dxA: number[] = new Array(n - 1);
      const slope: number[] = new Array(n - 1);
      for (let i = 0; i < n - 1; i++) {
        dxA[i] = pxX[i + 1] - pxX[i];
        slope[i] = dxA[i] !== 0 ? (pxY[i + 1] - pxY[i]) / dxA[i] : 0;
      }

      // Tangent slopes at each point
      const m: number[] = new Array(n);
      m[0] = slope[0];
      m[n - 1] = slope[n - 2];
      for (let i = 1; i < n - 1; i++) {
        if (slope[i - 1] * slope[i] <= 0) {
          m[i] = 0; // sign change — flat tangent
        } else {
          m[i] = (slope[i - 1] + slope[i]) / 2;
        }
      }

      // Fritsch-Carlson monotonicity clamp: α² + β² ≤ 9
      for (let i = 0; i < n - 1; i++) {
        if (Math.abs(slope[i]) < 1e-12) {
          m[i] = 0;
          m[i + 1] = 0;
        } else {
          const ak = m[i] / slope[i];
          const bk = m[i + 1] / slope[i];
          const mag = ak * ak + bk * bk;
          if (mag > 9) {
            const s = 3 / Math.sqrt(mag);
            m[i]     = s * ak * slope[i];
            m[i + 1] = s * bk * slope[i];
          }
        }
      }

      // Emit cubic Bezier segments
      for (let i = 0; i < n - 1; i++) {
        const seg = dxA[i] / 3;
        ctx.bezierCurveTo(
          pxX[i] + seg,     pxY[i] + m[i] * seg,
          pxX[i + 1] - seg, pxY[i + 1] - m[i + 1] * seg,
          pxX[i + 1],       pxY[i + 1],
        );
      }
    }

    ctx.stroke();

    if (opts?.fill && n >= 2) {
      ctx.globalAlpha = 0.06;
      ctx.fillStyle = strokeColor;
      // Close the fill area down to the baseline — only real data extent
      ctx.lineTo(pxX[n - 1], cy + plotH);
      ctx.lineTo(pxX[0], cy + plotH);
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  };

  // main series
  drawSeries(visible, color, { fill: true });

  // overlays (other devices)
  if (overlays && overlays.length) {
    for (const ov of overlays) {
      drawSeries(ov.data, ov.color ?? '#9ca3af', { fill: false });
    }
  }

  ctx.restore(); // unclip

  /* -- Latest value badge (right edge) ------------------------------- */
  ctx.shadowBlur = 0;
  // Determine latest point: prefer main series visible point, else find latest across overlays
  let last: TimeSeriesPoint | null = null;
  if (visible.length > 0) last = visible[visible.length - 1];
  if (!last && overlays && overlays.length) {
    for (const ov of overlays) {
      for (let i = ov.data.length - 1; i >= 0; i--) {
        const p = ov.data[i];
        if (p.ts >= tMin && p.ts <= tMax) { last = p; break; }
      }
      if (last) break;
    }
  }

  if (last) {
    // Pulsing dot at the last real data point (leading edge of the trace)
    const lx = cx + ((last.ts - tMin) / windowMs) * plotW;
    const ly = cy + plotH * (1 - (last.value - vMin) / vRange);

    const pulse = 2.5 + Math.sin(Date.now() / 300) * 1.2;
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(lx, ly, pulse, 0, Math.PI * 2); ctx.fill();

    // Value label
    ctx.fillStyle = '#e5e7eb';
    ctx.font = 'bold 10px "SF Mono", "Cascadia Code", Menlo, monospace';
    ctx.textAlign = 'right';
    const valStr = Math.abs(last.value) >= 100
      ? String(Math.round(last.value))
      : Number.isInteger(last.value) ? String(last.value) : last.value.toFixed(1);
    ctx.fillText(valStr + unit, lx - 8, Math.max(ly - 8, cy + 12));

    /* -- Cardiogram leading-edge glow --------------------------------- */
    if (lx > cx && lx <= cx + plotW) {
      /* Trailing neon glow behind the waveform head */
      const glowW = Math.min(48, lx - cx);
      if (glowW > 0) {
        const trailGrad = ctx.createLinearGradient(lx - glowW, 0, lx, 0);
        trailGrad.addColorStop(0, 'transparent');
        trailGrad.addColorStop(1, hexToRgba(color, 0.08));
        ctx.fillStyle = trailGrad;
        ctx.fillRect(lx - glowW, cy, glowW, plotH);
      }
      /* Thin sweep line at the head position */
      ctx.strokeStyle = hexToRgba(color, 0.18);
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(lx, cy); ctx.lineTo(lx, cy + plotH); ctx.stroke();
    }
  }

  /* -- Hover crosshair + tooltip -------------------------------------- */
  if (hoverTs !== null && hoverTs >= tMin && hoverTs <= tMax) {
    const hx = cx + ((hoverTs - tMin) / windowMs) * plotW;

    /* Vertical crosshair */
    ctx.strokeStyle = 'rgba(255,255,255,0.25)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(Math.round(hx) + 0.5, cy);
    ctx.lineTo(Math.round(hx) + 0.5, cy + plotH);
    ctx.stroke();
    ctx.setLineDash([]);

    /* Interpolated dots + tooltip content */
    const tooltipLines: string[] = [fmtTimeTooltip(hoverTs)];

    const mainVal = interpolateAt(data, hoverTs);
    if (mainVal !== null) {
      const hy = cy + plotH * (1 - (mainVal - vMin) / vRange);
      /* Neon dot */
      ctx.fillStyle = color;
      ctx.shadowColor = color; ctx.shadowBlur = 8;
      ctx.beginPath(); ctx.arc(hx, hy, 4.5, 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0;
      /* Horizontal guide */
      ctx.strokeStyle = hexToRgba(color, 0.18);
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 4]);
      ctx.beginPath();
      ctx.moveTo(cx, Math.round(hy) + 0.5);
      ctx.lineTo(cx + plotW, Math.round(hy) + 0.5);
      ctx.stroke();
      ctx.setLineDash([]);
      const fv = Math.abs(mainVal) >= 100 ? String(Math.round(mainVal)) : mainVal.toFixed(1);
      tooltipLines.push(`${title}: ${fv}${unit}`);
    }

    if (overlays && overlays.length) {
      for (const ov of overlays) {
        const ovVal = interpolateAt(ov.data, hoverTs);
        if (ovVal !== null) {
          const hy = cy + plotH * (1 - (ovVal - vMin) / vRange);
          const oc = ov.color ?? '#9ca3af';
          ctx.fillStyle = oc;
          ctx.shadowColor = oc; ctx.shadowBlur = 6;
          ctx.beginPath(); ctx.arc(hx, hy, 3.5, 0, Math.PI * 2); ctx.fill();
          ctx.shadowBlur = 0;
          const fv = Math.abs(ovVal) >= 100 ? String(Math.round(ovVal)) : ovVal.toFixed(1);
          tooltipLines.push(`${ov.label}: ${fv}${unit}`);
        }
      }
    }

    /* Tooltip glass box */
    if (tooltipLines.length > 1) {
      const font = '10px "SF Mono", "Cascadia Code", Menlo, monospace';
      ctx.font = font;
      const lineH = 15, padX = 8, padY = 6;
      let maxTW = 0;
      for (const ln of tooltipLines) { const m = ctx.measureText(ln).width; if (m > maxTW) maxTW = m; }
      const boxW = maxTW + padX * 2;
      const boxH = tooltipLines.length * lineH + padY * 2;
      const gap = 12;
      let bx = hx + gap;
      if (bx + boxW > cx + plotW) bx = hx - gap - boxW;
      const by = cy + 8;
      ctx.fillStyle = 'rgba(0,0,0,0.82)';
      ctx.strokeStyle = 'rgba(255,255,255,0.10)';
      ctx.lineWidth = 1;
      roundedRect(ctx, bx, by, boxW, boxH, 4);
      ctx.fill();
      ctx.stroke();
      ctx.textAlign = 'left';
      for (let i = 0; i < tooltipLines.length; i++) {
        ctx.fillStyle = i === 0 ? '#8b949e' : '#e5e7eb';
        ctx.fillText(tooltipLines[i], bx + padX, by + padY + (i + 1) * lineH - 4);
      }
    }
  }

  return tMax;
}

/* -- React Component -------------------------------------------------- */

const TelemetryTimeSeriesPlot: React.FC<TelemetryTimeSeriesPlotProps> = ({
  data,
  title,
  unit = '',
  color = '#34d399',
  className,
  minHeight = 340,
  windowSec = 300,
  overlays = [],
  hoverTsRef,
  yFloor,
  yCeil,
  markers,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef    = useRef<HTMLCanvasElement | null>(null);
  const rafRef       = useRef<number>(0);
  const sizeRef      = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  /** Animated time-axis zoom — lerps from old→new window over 320ms
   *  with ease-out cubic for buttery smooth window transitions.       */
  const prevWsRef = useRef(windowSec);
  const animRef   = useRef<{ fromMs: number; toMs: number; startMs: number } | null>(null);

  /** Last tMax computed by drawFrame — hover handler reads this so the
   *  crosshair aligns with the same time-axis the canvas painted. */
  const tMaxRef = useRef<number>(Date.now());

  /* -- Hover sync: shared ref (cross-plot) or local fallback --------- */
  const _localHoverRef = useRef<number | null>(null);

  /* -- Stable refs for current props --------------------------------- */
  const propsRef = useRef({ data, title, unit, color, windowSec, overlays: undefined as undefined | Array<{ label: string; data: TimeSeriesPoint[]; color?: string }>, hoverTsRef: undefined as undefined | React.MutableRefObject<number | null>, yFloor: undefined as number | undefined, yCeil: undefined as number | undefined, markers: undefined as PlotMarker[] | undefined });
  propsRef.current = { data, title, unit, color, windowSec, overlays, hoverTsRef, yFloor, yCeil, markers };

  /* -- Paint function (reads from refs, never a dep) ----------------- */
  const paint = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { w: cw, h: ch } = sizeRef.current;
    if (cw === 0 || ch === 0) return;

    const dpr = window.devicePixelRatio || 1;
    const needsResize = canvas.width !== cw * dpr || canvas.height !== ch * dpr;
    if (needsResize) {
      canvas.width  = cw * dpr;
      canvas.height = ch * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    const { data: d, title: t, unit: u, color: c, windowSec: ws, overlays: ovs, hoverTsRef: hr, yFloor: yf, yCeil: yc, anchorTs: aTs, markers: mks } = propsRef.current as any;

    // ── Animated time-axis zoom ─────────────────────────────────────────
    // When windowSec changes, smoothly lerp from old→new over 320ms
    // with ease-out cubic.  The chart "zooms out" instead of jumping.
    if (ws !== prevWsRef.current) {
      animRef.current = { fromMs: prevWsRef.current * 1000, toMs: ws * 1000, startMs: performance.now() };
      prevWsRef.current = ws;
    }
    let effectiveWindowMs = ws * 1000;
    const anim = animRef.current;
    if (anim) {
      const frac = Math.min(1, (performance.now() - anim.startMs) / 320);
      const eased = 1 - (1 - frac) * (1 - frac) * (1 - frac); // ease-out cubic
      effectiveWindowMs = anim.fromMs + (anim.toMs - anim.fromMs) * eased;
      if (frac >= 1) animRef.current = null;
    }

    const hts: number | null = hr?.current ?? _localHoverRef.current;
    tMaxRef.current = drawFrame(ctx, cw, ch, d, t, u, c, ovs ?? [], effectiveWindowMs, hts, yf, yc, aTs, mks);
  };

  /* -- Continuous RAF loop + hover tracking --------------------------- */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const rect = container.getBoundingClientRect();
      sizeRef.current = { w: Math.round(rect.width), h: Math.round(rect.height) };
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    ro.observe(container);

    /* Mouse → hover-timestamp (shared across synced plots) ----------- */
    const onMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const { w: cw } = sizeRef.current;
      if (cw === 0) return;
      const plotW = cw - PAD.left - PAD.right;
      const frac = (x - PAD.left) / plotW;
      if (frac < 0 || frac > 1) { _localHoverRef.current = null; const hr = propsRef.current.hoverTsRef; if (hr) hr.current = null; return; }
      const ws = propsRef.current.windowSec;
      const windowMs = ws * 1000;
      const tNow = tMaxRef.current;  // align with the painted time-axis
      const ts = tNow - windowMs + frac * windowMs;
      _localHoverRef.current = ts;
      const hr = propsRef.current.hoverTsRef;
      if (hr) hr.current = ts;
    };
    const onMouseLeave = () => {
      _localHoverRef.current = null;
      const hr = propsRef.current.hoverTsRef;
      if (hr) hr.current = null;
    };

    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mouseleave', onMouseLeave);

    let running = true;
    const loop = () => {
      if (!running) return;
      paint();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      running = false;
      ro.disconnect();
      cancelAnimationFrame(rafRef.current);
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * Container = position:relative with FIXED height (minHeight).
   * Canvas    = position:absolute, inset:0 -- fills the box without
   *             affecting layout.  This eliminates the resize<->scrollbar
   *             feedback loop that caused the jittering.
   */
  return (
    <div
      ref={containerRef}
      className={`relative w-full rounded border border-white/[0.06] overflow-hidden${className ? ` ${className}` : ''}`}
      style={{ height: minHeight }}
    >
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, display: 'block', width: '100%', height: '100%' }}
      />
    </div>
  );
};

export default TelemetryTimeSeriesPlot;